import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography, FormControl, InputLabel, Select, OutlinedInput, Box as MBox,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, extractError } from '../../../shared/api';
import type { Subject, Teacher } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { initials } from '../../../shared/utils';

export default function TeacherListPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Teacher | null>(null);
  const [creating, setCreating] = useState(false);

  const teachersQ = useQuery({ queryKey: ['teachers'], queryFn: async () => (await api.get<Teacher[]>('/admin/teachers')).data });
  const subjectsQ = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<Subject[]>('/subjects')).data });
  const subjectMap = new Map((subjectsQ.data ?? []).map((s) => [s.id, s.name]));

  const del = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/teachers/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teachers'] }),
  });

  return (
    <Box>
      <PageHeader
        title="Teachers"
        subtitle={`${teachersQ.data?.length ?? 0} faculty members`}
        action={isAdmin && <Button startIcon={<Add />} variant="contained" onClick={() => setCreating(true)}>Add teacher</Button>}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Subjects</TableCell>
              <TableCell>Qualification</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(teachersQ.data ?? []).map((t) => (
              <TableRow key={t.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main' }}>{initials(`${t.firstName} ${t.lastName}`)}</Avatar>
                    <Typography sx={{ fontWeight: 600 }}>{t.firstName} {t.lastName}</Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{t.employeeId}</TableCell>
                <TableCell>
                  {t.subjectIds.map((sid) => <Chip key={sid} size="small" label={subjectMap.get(sid) ?? sid} sx={{ mr: 0.5 }} />)}
                </TableCell>
                <TableCell>{t.qualification}</TableCell>
                <TableCell>{t.email}</TableCell>
                <TableCell>{t.phone}</TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setEditing(t)}><Edit /></IconButton></Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => { if (window.confirm(`Delete ${t.firstName}?`)) del.mutate(t.id); }}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {(teachersQ.data?.length ?? 0) === 0 && (
              <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No teachers yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TeacherForm open={creating || !!editing} teacher={editing} subjects={subjectsQ.data ?? []} onClose={() => { setCreating(false); setEditing(null); }} />
    </Box>
  );
}

function TeacherForm({ open, teacher, subjects, onClose }: { open: boolean; teacher: Teacher | null; subjects: Subject[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<Teacher>>(teacher ?? { subjectIds: [] });
  const isEdit = !!teacher;
  const set = (k: keyof Teacher, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) return (await api.put(`/admin/teachers/${teacher!.id}`, form)).data;
      return (await api.post('/admin/teachers', form)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['teachers'] }); onClose(); },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionProps={{ onEntered: () => setForm(teacher ?? { subjectIds: [] }) }}>
      <DialogTitle>{isEdit ? 'Edit teacher' : 'Add teacher'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }} direction={{ md: 'row' }} flexWrap="wrap">
          <TextField label="Employee ID" value={form.employeeId ?? ''} onChange={(e) => set('employeeId', e.target.value)} required disabled={isEdit} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="First name" value={form.firstName ?? ''} onChange={(e) => set('firstName', e.target.value)} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Last name" value={form.lastName ?? ''} onChange={(e) => set('lastName', e.target.value)} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Email" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} required sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Phone" value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Qualification" value={form.qualification ?? ''} onChange={(e) => set('qualification', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <TextField label="Joined on" type="date" InputLabelProps={{ shrink: true }} value={form.joinedOn ?? ''} onChange={(e) => set('joinedOn', e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
          <FormControl sx={{ flex: 1, minWidth: 260 }}>
            <InputLabel>Subjects</InputLabel>
            <Select
              multiple value={form.subjectIds ?? []} input={<OutlinedInput label="Subjects" />}
              onChange={(e) => set('subjectIds', e.target.value as string[])}
              renderValue={(selected) => (selected as string[]).map((sid) => subjects.find((s) => s.id === sid)?.name).join(', ')}
            >
              {subjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </Select>
          </FormControl>
          {save.isError && <MBox sx={{ width: '100%' }}><Alert severity="error">{extractError(save.error)}</Alert></MBox>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add teacher'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
